import {
  Search,
  MessageCircle,
  BarChart3,
  TrendingUp,
  Users,
  Building2,
  GraduationCap,
  HeartHandshake,
  Boxes,
  Sparkles,
  Activity,
  BriefcaseBusiness,
  LayoutDashboard,
  Settings2,
  UserRoundSearch,
  Wallet,
  Calculator,
  MapPinned,
  CalendarDays,
  ShoppingBasket,
  CookingPot,
  UtensilsCrossed,
  Store,
  MapPin,
  Smartphone,
  RouteIcon,
  Bell,
  BadgePercent,
  CreditCard,
  CarFront,
  Plane,
  Car,
  Star,
  Palette,
  ClipboardCheck,
  Languages,
  Video,
  BookOpen,
  Bike,
  Compass,
} from "lucide-react";

import type { ServicePage } from "@/types/service";

export const OnDemandMobileAppDevelopments: ServicePage[] = [
  // Food Delivery App Development
  {
    title: "Food Delivery App Development",
    slug: "food-delivery-app-development",
    group: "On Demand Mobile App",
    icon: Store,

    seo: {
      title: "Food Delivery App Development Services",
      description:
        "Build scalable food delivery applications with customer, restaurant, delivery partner, dispatch, payment, promotion, and real-time order management systems.",
      keywords: [
        "Food Delivery App Development",
        "Food Delivery App Development Services",
        "On Demand Food Delivery App",
        "Restaurant Delivery App Development",
        "Food Delivery App Developers",
        "Food Delivery App Development Company",
      ],
    },

    hero: {
      title: "Food Delivery App Development for End-to-End Delivery Operations",
      description:
        "Launch a delivery marketplace with operations tooling from order placement to last-mile fulfillment.",
      highlights: [
        "Multi-Role Mobile Apps",
        "Live Order Tracking",
        "Admin & Merchant Dashboards",
        "Delivery Operations",
      ],
    },

    whyChoose: {
      title: "Why Invest in a Food Delivery App?",
      description:
        "A custom food delivery platform connects customers, restaurants, delivery partners, payments, dispatch, and administrative operations within one coordinated digital ecosystem.",
      items: [
        {
          title: "Multi-Role Ecosystem",
          description:
            "Connect customers, restaurants, delivery partners, and administrators through dedicated experiences and coordinated workflows.",
          icon: Users,
        },
        {
          title: "Real-Time Order Visibility",
          description:
            "Keep relevant users informed as orders move through confirmation, preparation, pickup, delivery, and completion stages.",
          icon: MapPin,
        },
        {
          title: "Centralized Operations",
          description:
            "Manage restaurants, orders, delivery partners, promotions, payments, users, and platform activity from administrative dashboards.",
          icon: LayoutDashboard,
        },
        {
          title: "Scalable Delivery Operations",
          description:
            "Build a platform capable of supporting additional restaurants, customers, delivery partners, locations, and order volumes.",
          icon: TrendingUp,
        },
      ],
    },

    services: {
      title: "Our Food Delivery App Development Services",
      description:
        "End-to-end food delivery development covering customer apps, restaurant systems, delivery partner apps, dispatch, payments, tracking, and administrative operations.",
      items: [
        {
          title: "Customer Food Delivery App",
          description:
            "Create intuitive customer experiences for discovering restaurants, browsing menus, placing orders, making payments, and tracking deliveries.",
          icon: Smartphone,
          features: [
            "Restaurant Discovery",
            "Menu Browsing",
            "Cart & Checkout",
            "Order Tracking",
            "Ratings & Reviews",
          ],
        },
        {
          title: "Restaurant & Merchant Platform",
          description:
            "Give restaurants tools to manage menus, availability, incoming orders, preparation workflows, promotions, and business information.",
          icon: Store,
          features: [
            "Restaurant Dashboard",
            "Menu Management",
            "Order Management",
            "Availability Controls",
            "Promotion Management",
          ],
        },
        {
          title: "Delivery Partner App",
          description:
            "Provide delivery partners with suitable tools for availability, delivery requests, navigation, order status, and earnings visibility.",
          icon: Bike,
          features: [
            "Delivery Requests",
            "Availability Management",
            "Pickup & Delivery Status",
            "Navigation",
            "Earnings Overview",
          ],
        },
        {
          title: "Food Delivery Admin Panel",
          description:
            "Centralize platform operations with tools for managing users, restaurants, delivery partners, orders, payments, promotions, and support workflows.",
          icon: LayoutDashboard,
          features: [
            "User Management",
            "Merchant Management",
            "Delivery Partner Management",
            "Order Monitoring",
            "Platform Reporting",
          ],
        },
      ],
    },

    features: {
      title: "Key Food Delivery App Features",
      description:
        "Core functionality for delivering convenient customer experiences while supporting restaurants, delivery partners, and platform operations.",
      items: [
        {
          title: "Restaurant & Menu Discovery",
          description:
            "Help customers discover restaurants, browse menus, review item information, and select suitable ordering options.",
          icon: Search,
        },
        {
          title: "Cart & Secure Payments",
          description:
            "Support structured cart, checkout, pricing, discounts, payment, and order confirmation workflows.",
          icon: CreditCard,
        },
        {
          title: "Live Order Tracking",
          description:
            "Provide relevant order and delivery status visibility from restaurant confirmation through last-mile fulfillment.",
          icon: MapPin,
        },
        {
          title: "Order & Dispatch Management",
          description:
            "Coordinate incoming orders, restaurant preparation, delivery assignment, pickup, and fulfillment workflows.",
          icon: RouteIcon,
        },
        {
          title: "Offers & Promotions",
          description:
            "Manage suitable coupons, promotional campaigns, discounts, and customer offers through controlled platform workflows.",
          icon: BadgePercent,
        },
        {
          title: "Push Notifications",
          description:
            "Keep users informed about relevant order updates, delivery activity, offers, and platform communications.",
          icon: Bell,
        },
      ],
    },

    process: {
      title: "Our Food Delivery App Development Process",
      description:
        "A structured development process focused on marketplace roles, ordering workflows, delivery operations, real-time tracking, payments, and scalable platform architecture.",
      items: [
        {
          title: "Business & Marketplace Discovery",
          description:
            "Understand your delivery model, target market, restaurants, customers, delivery partners, service areas, payments, commissions, and operational requirements.",
        },
        {
          title: "Workflow & Architecture Planning",
          description:
            "Define user roles, ordering flows, restaurant operations, dispatch logic, delivery workflows, payments, notifications, APIs, and platform architecture.",
        },
        {
          title: "UI/UX Design",
          description:
            "Design customer, restaurant, delivery partner, and administrative experiences around clear ordering and fulfillment journeys.",
        },
        {
          title: "Platform Development",
          description:
            "Develop mobile applications, backend services, dashboards, order workflows, payments, maps, notifications, and required integrations.",
        },
        {
          title: "Testing & Operational Validation",
          description:
            "Test ordering, payments, restaurant workflows, dispatch, tracking, delivery statuses, notifications, permissions, and multi-role scenarios.",
        },
        {
          title: "Deployment & Support",
          description:
            "Launch the platform and provide ongoing technical support, optimization, integrations, operational improvements, and additional functionality.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Custom Food Delivery App Development",
      description:
        "Create a connected delivery ecosystem that improves ordering convenience while giving restaurants, delivery partners, and administrators structured operational tools.",
      items: [
        {
          title: "Convenient Customer Ordering",
          description:
            "Give customers a streamlined way to discover restaurants, browse menus, order food, pay, and monitor delivery progress.",
          icon: Smartphone,
        },
        {
          title: "Connected Restaurant Operations",
          description:
            "Provide merchants with structured tools for menus, orders, preparation statuses, availability, and promotions.",
          icon: Store,
        },
        {
          title: "Efficient Delivery Workflows",
          description:
            "Coordinate suitable delivery assignments, pickups, navigation, status updates, and fulfillment processes.",
          icon: RouteIcon,
        },
        {
          title: "Centralized Platform Control",
          description:
            "Manage users, merchants, delivery partners, orders, payments, promotions, and operational activity from one platform.",
          icon: LayoutDashboard,
        },
        {
          title: "Real-Time Visibility",
          description:
            "Keep relevant participants informed about order progression, preparation, pickup, and delivery activity.",
          icon: Activity,
        },
        {
          title: "Scalable Marketplace Growth",
          description:
            "Expand restaurants, delivery partners, customers, locations, functionality, and order capacity as the platform grows.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "Food Delivery App Development Technologies",
      description:
        "Technologies used to build cross-platform mobile experiences, location services, backend operations, and real-time user communication.",
      technologies: ["React Native", "Maps API", "Node.js", "Firebase"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We approach food delivery development as a complete multi-role marketplace, connecting customer experiences with restaurant operations, delivery workflows, payments, tracking, and platform administration.",
      items: [
        {
          title: "Multi-Role App Development",
          description:
            "Build coordinated experiences for customers, restaurants, delivery partners, administrators, and other required platform roles.",
          icon: Users,
        },
        {
          title: "Real-Time Delivery Engineering",
          description:
            "Implement suitable location, tracking, order status, notification, and fulfillment workflows for delivery operations.",
          icon: MapPin,
        },
        {
          title: "Operations-Focused Architecture",
          description:
            "Design backend systems around ordering, restaurant workflows, dispatch, delivery management, payments, and platform control.",
          icon: Settings2,
        },
        {
          title: "Payment & Integration Expertise",
          description:
            "Connect suitable payment gateways, maps, notifications, APIs, and third-party services required by the platform.",
          icon: Boxes,
        },
        {
          title: "Scalable Marketplace Development",
          description:
            "Build technical foundations capable of supporting increasing users, restaurants, delivery partners, locations, and orders.",
          icon: TrendingUp,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support the platform with troubleshooting, optimization, integrations, new functionality, and operational improvements.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "Food Delivery Solutions for Different Business Models",
      description:
        "Custom food delivery platforms tailored to different restaurant networks, ordering models, delivery operations, and marketplace requirements.",
      items: [
        {
          title: "Multi-Restaurant Marketplaces",
          description:
            "Connect multiple restaurants with customers and delivery partners through centralized ordering, payments, dispatch, and platform management.",
          icon: Store,
        },
        {
          title: "Restaurant-Owned Delivery",
          description:
            "Build branded ordering and delivery platforms for restaurant businesses managing their own menus, customers, orders, and fulfillment.",
          icon: UtensilsCrossed,
        },
        {
          title: "Cloud Kitchens",
          description:
            "Manage suitable brands, menus, orders, preparation workflows, delivery operations, and customer experiences from centralized systems.",
          icon: CookingPot,
        },
        {
          title: "Grocery & Local Food Delivery",
          description:
            "Create ordering platforms for suitable local products, catalogs, carts, payments, delivery scheduling, and fulfillment.",
          icon: ShoppingBasket,
        },
        {
          title: "Meal Subscription Services",
          description:
            "Support suitable recurring meal plans, customer preferences, schedules, payments, order management, and delivery workflows.",
          icon: CalendarDays,
        },
        {
          title: "Regional Delivery Platforms",
          description:
            "Build localized delivery marketplaces around defined service areas, restaurants, delivery partners, pricing, and operational requirements.",
          icon: MapPinned,
        },
      ],
    },

    portfolio: {
      title: "Food Delivery App Development Success Stories",
      description:
        "Food delivery platforms focused on multi-role experiences, real-time operations, ordering, payments, dispatch, and last-mile fulfillment.",
      items: [
        {
          title: "Multi-Restaurant Delivery Marketplace",
          description:
            "Developed customer and delivery applications with restaurant dashboards, menu management, ordering, payments, live delivery tracking, promotions, and centralized administration.",
          image: "/project-image.webp",
        },
        {
          title: "Restaurant Delivery Platform",
          description:
            "Built a branded ordering system connecting customer ordering, restaurant preparation workflows, delivery assignments, payments, notifications, and order tracking.",
          image: "/project-image.webp",
        },
        {
          title: "Local Food Delivery Network",
          description:
            "Created a multi-role delivery platform with merchant onboarding, customer ordering, delivery partner workflows, location services, dispatch, and operational dashboards.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Food Delivery App Developers",
      description:
        "Partner with our developers to design, build, integrate, optimize, and support food delivery platforms connecting customers, restaurants, delivery partners, and operations teams.",
      cta: "Let's Build Your Food Delivery App",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to translate complex food ordering and delivery operations into connected digital platforms.",
      items: [
        {
          name: "Daniel Wilson",
          company: "UrbanPlate",
          location: "United Kingdom",
          review:
            "The team understood that we needed much more than a customer ordering app. They connected our restaurant operations, delivery workflows, tracking, and admin controls into one platform.",
        },
        {
          name: "Michael Carter",
          company: "QuickBite Delivery",
          location: "United States",
          review:
            "Skand Tech Hub built the customer, merchant, and delivery workflows around how our operation actually works. Real-time order visibility has made managing deliveries significantly easier.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description:
        "Common questions about our Food Delivery App Development services.",
      items: [
        {
          question:
            "What applications are included in a food delivery platform?",
          answer:
            "Depending on your business model, a food delivery platform can include customer applications, restaurant or merchant systems, delivery partner applications, administrative dashboards, and backend services connecting all platform operations.",
        },
        {
          question: "Can you build live order tracking?",
          answer:
            "Yes. Suitable map and location services can be integrated to provide relevant order and delivery status visibility throughout the fulfillment process.",
        },
        {
          question: "Can restaurants manage their own menus and orders?",
          answer:
            "Yes. Merchant dashboards can provide suitable tools for menu management, pricing, availability, incoming orders, preparation statuses, promotions, and restaurant information.",
        },
        {
          question: "Can delivery partners have a separate app?",
          answer:
            "Yes. Delivery partners can have dedicated functionality for availability, delivery requests, pickup and delivery statuses, navigation, earnings information, and other required workflows.",
        },
        {
          question: "Can payment gateways be integrated?",
          answer:
            "Yes. Suitable payment gateways can be integrated according to your target market, platform requirements, transaction flows, and payment provider configuration.",
        },
        {
          question: "Do you provide ongoing support after launch?",
          answer:
            "Yes. We can provide troubleshooting, performance optimization, integrations, feature enhancements, operational improvements, and ongoing food delivery platform development.",
        },
      ],
    },
  },
  // Taxi Booking App Development
  {
    title: "Taxi App Development",
    slug: "taxi-app-development",
    group: "On Demand Mobile App",
    icon: Car,

    seo: {
      title: "Taxi App Development Services",
      description:
        "Build scalable taxi booking applications with rider and driver apps, real-time tracking, dispatch logic, fare management, payments, and centralized operations dashboards.",
      keywords: [
        "Taxi App Development",
        "Taxi App Development Services",
        "Ride Booking App Development",
        "Taxi Booking App Development",
        "Taxi App Developers",
        "Taxi App Development Company",
      ],
    },

    hero: {
      title: "Taxi App Development for Real-Time Mobility Operations",
      description:
        "Build a mobility platform with real-time location, pricing controls, and reliable dispatch operations.",
      highlights: [
        "Rider & Driver Apps",
        "Live Tracking & Fare Logic",
        "Operations Dashboard",
        "Real-Time Dispatch",
      ],
    },

    whyChoose: {
      title: "Why Invest in a Custom Taxi App?",
      description:
        "A custom taxi platform connects riders, drivers, dispatch, pricing, payments, location services, and administrative operations within one coordinated mobility ecosystem.",
      items: [
        {
          title: "Real-Time Ride Operations",
          description:
            "Coordinate ride requests, driver availability, assignments, pickups, trips, and completion through connected workflows.",
          icon: Activity,
        },
        {
          title: "Live Location Tracking",
          description:
            "Use location services to provide relevant driver, pickup, route, and trip visibility throughout the ride lifecycle.",
          icon: MapPin,
        },
        {
          title: "Flexible Fare Management",
          description:
            "Configure suitable base fares, distance and time calculations, service charges, pricing rules, and other fare components.",
          icon: Calculator,
        },
        {
          title: "Centralized Operations",
          description:
            "Manage riders, drivers, trips, pricing, payments, service areas, and platform activity through administrative dashboards.",
          icon: LayoutDashboard,
        },
      ],
    },

    services: {
      title: "Our Taxi App Development Services",
      description:
        "End-to-end taxi app development covering rider experiences, driver applications, dispatch systems, maps, payments, fare management, and centralized platform operations.",
      items: [
        {
          title: "Rider App Development",
          description:
            "Create convenient rider experiences for booking trips, selecting pickup and destination locations, viewing fares, tracking drivers, and making payments.",
          icon: Smartphone,
          features: [
            "Ride Booking",
            "Pickup & Destination",
            "Fare Estimates",
            "Live Driver Tracking",
            "Trip History",
          ],
        },
        {
          title: "Driver App Development",
          description:
            "Provide drivers with tools for availability, ride requests, navigation, trip management, earnings, and account activity.",
          icon: Car,
          features: [
            "Driver Availability",
            "Ride Requests",
            "Navigation",
            "Trip Management",
            "Earnings Overview",
          ],
        },
        {
          title: "Dispatch & Ride Management",
          description:
            "Develop backend workflows for matching suitable drivers with ride requests and coordinating trip operations.",
          icon: RouteIcon,
          features: [
            "Driver Matching",
            "Ride Assignment",
            "Trip Status Management",
            "Location Updates",
            "Dispatch Controls",
          ],
        },
        {
          title: "Taxi Admin Panel",
          description:
            "Centralize mobility operations with tools for managing riders, drivers, trips, pricing, payments, service areas, and platform reporting.",
          icon: LayoutDashboard,
          features: [
            "Rider Management",
            "Driver Management",
            "Trip Monitoring",
            "Fare Management",
            "Operations Reporting",
          ],
        },
      ],
    },

    features: {
      title: "Key Taxi App Features",
      description:
        "Core functionality for connecting riders and drivers while supporting real-time dispatch, location tracking, payments, and mobility operations.",
      items: [
        {
          title: "Ride Booking",
          description:
            "Allow riders to request trips using suitable pickup, destination, ride type, and booking information.",
          icon: Car,
        },
        {
          title: "Real-Time Location Tracking",
          description:
            "Provide relevant driver location, arrival, route, and trip progress information through integrated map services.",
          icon: MapPin,
        },
        {
          title: "Fare Calculation",
          description:
            "Calculate suitable trip pricing using configured base fares, distance, duration, service types, and applicable pricing rules.",
          icon: Calculator,
        },
        {
          title: "Driver Matching & Dispatch",
          description:
            "Identify suitable available drivers and coordinate ride request, acceptance, pickup, and trip workflows.",
          icon: RouteIcon,
        },
        {
          title: "Payments",
          description:
            "Support suitable payment workflows for ride charges, transaction records, refunds, and platform payment operations.",
          icon: CreditCard,
        },
        {
          title: "Ratings & Reviews",
          description:
            "Collect suitable post-trip feedback to support rider and driver experience monitoring and platform quality controls.",
          icon: Star,
        },
      ],
    },

    process: {
      title: "Our Taxi App Development Process",
      description:
        "A structured mobility development process focused on rider and driver journeys, dispatch logic, location services, pricing, payments, and reliable trip operations.",
      items: [
        {
          title: "Mobility Business Discovery",
          description:
            "Understand your ride model, target market, vehicle categories, riders, drivers, service areas, pricing, commissions, payments, and operational requirements.",
        },
        {
          title: "Trip & System Architecture",
          description:
            "Define booking workflows, driver matching, trip states, location services, fare logic, payments, notifications, APIs, and platform architecture.",
        },
        {
          title: "Rider & Driver UI/UX Design",
          description:
            "Design rider booking journeys, driver workflows, map experiences, trip screens, payment flows, and administrative interfaces.",
        },
        {
          title: "Taxi Platform Development",
          description:
            "Develop rider and driver applications, backend services, dispatch logic, maps, payments, dashboards, notifications, and required integrations.",
        },
        {
          title: "Trip & Dispatch Testing",
          description:
            "Test booking, matching, location updates, fare calculations, trip statuses, payments, notifications, permissions, and real-world mobility scenarios.",
        },
        {
          title: "Deployment & Support",
          description:
            "Launch the taxi platform and provide ongoing technical support, optimization, integrations, operational improvements, and additional functionality.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Custom Taxi App Development",
      description:
        "Create a connected mobility platform that simplifies ride booking while giving drivers and operations teams the tools needed to manage trips efficiently.",
      items: [
        {
          title: "Convenient Ride Booking",
          description:
            "Give riders a streamlined experience for requesting rides, selecting locations, viewing trip information, and making payments.",
          icon: Smartphone,
        },
        {
          title: "Efficient Driver Operations",
          description:
            "Provide drivers with structured workflows for availability, ride requests, navigation, trip statuses, and earnings.",
          icon: Car,
        },
        {
          title: "Real-Time Trip Visibility",
          description:
            "Keep relevant users informed about driver location, arrival, pickup, route, and trip progression.",
          icon: MapPin,
        },
        {
          title: "Automated Dispatch Workflows",
          description:
            "Reduce manual coordination through suitable driver matching, ride assignment, and trip status workflows.",
          icon: RouteIcon,
        },
        {
          title: "Centralized Mobility Control",
          description:
            "Manage drivers, riders, trips, fares, payments, service areas, and operational activity from one platform.",
          icon: LayoutDashboard,
        },
        {
          title: "Scalable Mobility Operations",
          description:
            "Expand drivers, riders, service areas, vehicle categories, functionality, and trip capacity as the platform grows.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "Taxi App Development Technologies",
      description:
        "Technologies used to build cross-platform rider and driver apps, real-time location workflows, dispatch communication, and payment processing.",
      technologies: ["React Native", "Google Maps", "WebSockets", "Stripe"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We approach taxi app development as a complete mobility ecosystem, connecting rider experiences with driver operations, dispatch logic, maps, pricing, payments, and centralized administration.",
      items: [
        {
          title: "Multi-Role Mobility Development",
          description:
            "Build coordinated experiences for riders, drivers, administrators, dispatch teams, and other required platform roles.",
          icon: Users,
        },
        {
          title: "Real-Time Location Engineering",
          description:
            "Implement suitable map, location, trip tracking, driver movement, and real-time communication workflows.",
          icon: MapPin,
        },
        {
          title: "Dispatch & Trip Architecture",
          description:
            "Design backend systems around driver availability, matching, assignments, trip states, pricing, and operational control.",
          icon: RouteIcon,
        },
        {
          title: "Payment Integration",
          description:
            "Connect suitable payment systems with ride charges, transaction records, platform fees, and relevant payment workflows.",
          icon: CreditCard,
        },
        {
          title: "Scalable Platform Engineering",
          description:
            "Build technical foundations capable of supporting increasing riders, drivers, locations, trips, and real-time activity.",
          icon: TrendingUp,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support the platform with troubleshooting, optimization, integrations, new functionality, and mobility workflow improvements.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "Taxi App Solutions for Different Mobility Models",
      description:
        "Custom mobility platforms tailored to different ride services, vehicle categories, dispatch models, and transportation operations.",
      items: [
        {
          title: "On-Demand Taxi Services",
          description:
            "Connect riders with available drivers through booking, matching, live tracking, payments, ratings, and trip management.",
          icon: Car,
        },
        {
          title: "Private Hire Services",
          description:
            "Build branded booking platforms for private transportation businesses managing drivers, vehicles, customers, trips, and payments.",
          icon: BriefcaseBusiness,
        },
        {
          title: "Airport Transfer Services",
          description:
            "Support scheduled and on-demand airport transportation with booking, driver assignment, trip information, and payment workflows.",
          icon: Plane,
        },
        {
          title: "Corporate Transportation",
          description:
            "Create suitable employee and business travel workflows with bookings, account management, trip records, and centralized administration.",
          icon: Building2,
        },
        {
          title: "Multi-Vehicle Mobility",
          description:
            "Support suitable vehicle categories with configurable pricing, availability, booking rules, and service experiences.",
          icon: CarFront,
        },
        {
          title: "Regional Mobility Platforms",
          description:
            "Build localized taxi platforms around defined service areas, driver networks, pricing structures, and operational requirements.",
          icon: MapPinned,
        },
      ],
    },

    portfolio: {
      title: "Taxi App Development Success Stories",
      description:
        "Mobility platforms focused on rider and driver experiences, real-time tracking, dispatch, pricing, payments, and operational control.",
      items: [
        {
          title: "On-Demand Taxi Booking Platform",
          description:
            "Developed rider and driver applications with ride booking, real-time driver matching, live location tracking, fare calculations, payments, ratings, and centralized administration.",
          image: "/project-image.webp",
        },
        {
          title: "Private Ride Management System",
          description:
            "Built a branded mobility platform connecting customer bookings, driver availability, dispatch workflows, trip tracking, payments, and operational dashboards.",
          image: "/project-image.webp",
        },
        {
          title: "Multi-Service Mobility Platform",
          description:
            "Created a transportation platform supporting multiple vehicle categories, configurable pricing, driver workflows, real-time trips, payments, and administrative controls.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Taxi App Developers",
      description:
        "Partner with our developers to design, build, integrate, optimize, and support taxi booking platforms with rider apps, driver apps, dispatch, tracking, payments, and operations tools.",
      cta: "Let's Build Your Taxi App",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Businesses trust Skand Tech Hub to translate real-time mobility operations into connected rider, driver, and dispatch platforms.",
      items: [
        {
          name: "Andrew Thompson",
          company: "UrbanRide Mobility",
          location: "United Kingdom",
          review:
            "The rider and driver apps work together smoothly, but the biggest improvement for us has been the operations dashboard. Our team can now see trips, drivers, and ride activity much more clearly.",
        },
        {
          name: "Ryan Mitchell",
          company: "MetroCab Technologies",
          location: "United States",
          review:
            "Skand Tech Hub understood the importance of dispatch and real-time location from the beginning. The platform gives us much better control over driver availability, ride assignments, and trip operations.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our Taxi App Development services.",
      items: [
        {
          question:
            "What applications are included in a taxi booking platform?",
          answer:
            "Depending on your business model, a taxi platform can include rider applications, driver applications, administrative dashboards, dispatch tools, and backend services connecting bookings, trips, maps, pricing, payments, and notifications.",
        },
        {
          question: "Can you build real-time driver tracking?",
          answer:
            "Yes. Suitable map, location, and real-time communication technologies can be integrated to provide relevant driver and trip location updates.",
        },
        {
          question: "Can the app calculate fares automatically?",
          answer:
            "Yes. Fare logic can be configured around suitable base charges, distance, duration, vehicle categories, service fees, and other defined pricing rules.",
        },
        {
          question: "Can drivers have a separate mobile app?",
          answer:
            "Yes. Drivers can have dedicated functionality for availability, ride requests, navigation, trip management, earnings visibility, and other required workflows.",
        },
        {
          question: "Can payment gateways be integrated?",
          answer:
            "Yes. Suitable payment providers can be integrated according to your target market, transaction model, platform requirements, and provider capabilities.",
        },
        {
          question: "Do you provide ongoing taxi app support?",
          answer:
            "Yes. We can provide troubleshooting, optimization, integrations, feature enhancements, dispatch improvements, and ongoing taxi platform development.",
        },
      ],
    },
  },
  // Tutor App Development
  {
    title: "Tutor App Development",
    slug: "tutor-app-development",
    group: "On Demand Mobile App",
    icon: GraduationCap,

    seo: {
      title: "Tutor App Development Services",
      description:
        "Build tutor and learning marketplace apps with educator discovery, booking, live sessions, learning content, payments, student progress, and centralized management.",
      keywords: [
        "Tutor App Development",
        "Tutor App Development Services",
        "Online Tutoring App Development",
        "Tutor Booking App Development",
        "E-Learning App Development",
        "Tutor App Development Company",
      ],
    },

    hero: {
      title: "Tutor App Development for Connected Digital Learning",
      description:
        "Connect students and educators through a polished digital learning product.",
      highlights: [
        "Tutor Discovery & Booking",
        "Classroom & Content Workflows",
        "Progress & Payment Reporting",
        "Live Learning Sessions",
      ],
    },

    whyChoose: {
      title: "Why Invest in a Custom Tutor App?",
      description:
        "A custom tutor platform connects students, educators, learning sessions, content, scheduling, payments, and progress tracking within one structured digital learning ecosystem.",
      items: [
        {
          title: "Easy Tutor Discovery",
          description:
            "Help students discover suitable tutors based on subjects, expertise, availability, profiles, and other relevant criteria.",
          icon: Search,
        },
        {
          title: "Flexible Session Booking",
          description:
            "Allow students and tutors to coordinate suitable learning sessions through availability, scheduling, and booking workflows.",
          icon: CalendarDays,
        },
        {
          title: "Integrated Live Learning",
          description:
            "Connect students and educators through suitable real-time video, audio, communication, and classroom experiences.",
          icon: Video,
        },
        {
          title: "Learning Progress Visibility",
          description:
            "Track relevant sessions, learning activity, progress information, and educational outcomes through structured dashboards.",
          icon: BarChart3,
        },
      ],
    },

    services: {
      title: "Our Tutor App Development Services",
      description:
        "End-to-end tutor app development covering student experiences, educator portals, booking, live classes, learning content, payments, progress tracking, and platform administration.",
      items: [
        {
          title: "Student App Development",
          description:
            "Create student experiences for tutor discovery, booking, live sessions, learning content, payments, and progress tracking.",
          icon: GraduationCap,
          features: [
            "Tutor Discovery",
            "Session Booking",
            "Live Classes",
            "Learning Content",
            "Progress Tracking",
          ],
        },
        {
          title: "Tutor App & Portal Development",
          description:
            "Give tutors tools to manage profiles, subjects, availability, bookings, learning sessions, content, and earnings.",
          icon: UserRoundSearch,
          features: [
            "Tutor Profiles",
            "Availability Management",
            "Booking Management",
            "Class Management",
            "Earnings Overview",
          ],
        },
        {
          title: "Live Classroom Development",
          description:
            "Build suitable real-time learning experiences with video sessions, communication, class workflows, and supporting learning tools.",
          icon: Video,
          features: [
            "Video Sessions",
            "Audio Communication",
            "Class Scheduling",
            "Session Controls",
            "Real-Time Interaction",
          ],
        },
        {
          title: "Tutor Platform Admin Panel",
          description:
            "Centralize platform operations with tools for managing students, tutors, bookings, payments, content, reports, and platform activity.",
          icon: LayoutDashboard,
          features: [
            "Student Management",
            "Tutor Management",
            "Booking Management",
            "Payment Management",
            "Platform Reporting",
          ],
        },
      ],
    },

    features: {
      title: "Key Tutor App Features",
      description:
        "Core functionality for connecting students and educators through discovery, scheduling, live learning, content, payments, and progress workflows.",
      items: [
        {
          title: "Tutor Search & Discovery",
          description:
            "Allow students to discover suitable educators using relevant subjects, expertise, profiles, availability, and search criteria.",
          icon: Search,
        },
        {
          title: "Booking & Scheduling",
          description:
            "Coordinate tutor availability, student bookings, session schedules, cancellations, and relevant calendar workflows.",
          icon: CalendarDays,
        },
        {
          title: "Live Video Classes",
          description:
            "Enable suitable real-time learning sessions through integrated video, audio, and communication technologies.",
          icon: Video,
        },
        {
          title: "Learning Content",
          description:
            "Organize suitable study materials, resources, documents, lesson content, and other educational information.",
          icon: BookOpen,
        },
        {
          title: "Payments",
          description:
            "Support suitable session payments, transaction records, tutor earnings, platform charges, and related payment workflows.",
          icon: CreditCard,
        },
        {
          title: "Student Progress Tracking",
          description:
            "Maintain relevant information about completed sessions, learning activities, performance, and educational progress.",
          icon: TrendingUp,
        },
      ],
    },

    process: {
      title: "Our Tutor App Development Process",
      description:
        "A structured education platform development process focused on student and tutor journeys, booking, live sessions, content, payments, and learning management.",
      items: [
        {
          title: "Learning Platform Discovery",
          description:
            "Understand your education model, target students, tutors, subjects, session formats, booking rules, payments, content, and operational requirements.",
        },
        {
          title: "Learning Workflow & Architecture",
          description:
            "Define user roles, tutor discovery, scheduling, booking, classroom workflows, payments, content, notifications, APIs, and platform architecture.",
        },
        {
          title: "Student & Tutor UI/UX Design",
          description:
            "Design discovery, booking, classroom, learning content, progress, payment, tutor management, and administrative experiences.",
        },
        {
          title: "Tutor Platform Development",
          description:
            "Develop student and tutor applications, backend services, live classroom functionality, booking, payments, dashboards, and required integrations.",
        },
        {
          title: "Learning Workflow Testing",
          description:
            "Test tutor discovery, availability, bookings, live sessions, payments, content access, notifications, permissions, and progress workflows.",
        },
        {
          title: "Deployment & Support",
          description:
            "Launch the learning platform and provide ongoing technical support, optimization, integrations, educational features, and platform improvements.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Custom Tutor App Development",
      description:
        "Create a connected learning platform that simplifies tutor discovery and session management while giving educators and administrators structured digital tools.",
      items: [
        {
          title: "Accessible Learning",
          description:
            "Connect students with suitable educators and learning opportunities through mobile and digital experiences.",
          icon: GraduationCap,
        },
        {
          title: "Simplified Tutor Booking",
          description:
            "Coordinate availability, scheduling, bookings, reminders, and session management through structured workflows.",
          icon: CalendarDays,
        },
        {
          title: "Real-Time Learning Experiences",
          description:
            "Support suitable live educational interactions through integrated video, audio, and communication functionality.",
          icon: Video,
        },
        {
          title: "Centralized Learning Management",
          description:
            "Manage students, tutors, sessions, content, payments, and platform activity through connected systems.",
          icon: LayoutDashboard,
        },
        {
          title: "Progress Visibility",
          description:
            "Give relevant users access to suitable session history, learning activity, progress information, and educational reporting.",
          icon: BarChart3,
        },
        {
          title: "Scalable Education Marketplace",
          description:
            "Expand tutors, students, subjects, courses, sessions, functionality, and learning services as the platform grows.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "Tutor App Development Technologies",
      description:
        "Technologies used to build cross-platform learning experiences, real-time classrooms, backend services, and structured educational data.",
      technologies: ["React Native", "WebRTC", "Node.js", "PostgreSQL"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We approach tutor app development as a complete learning ecosystem, connecting student discovery with educator workflows, live sessions, scheduling, payments, content, and platform administration.",
      items: [
        {
          title: "Multi-Role Learning Platforms",
          description:
            "Build coordinated experiences for students, tutors, administrators, and other required educational platform roles.",
          icon: Users,
        },
        {
          title: "Real-Time Classroom Development",
          description:
            "Implement suitable video, audio, communication, scheduling, and session workflows for digital learning.",
          icon: Video,
        },
        {
          title: "Booking & Scheduling Architecture",
          description:
            "Design structured workflows around tutor availability, student bookings, session timing, cancellations, and reminders.",
          icon: CalendarDays,
        },
        {
          title: "Payment & Integration Expertise",
          description:
            "Connect suitable payment systems, communication technologies, notifications, APIs, and third-party services.",
          icon: Boxes,
        },
        {
          title: "Scalable Education Architecture",
          description:
            "Build technical foundations capable of supporting increasing students, tutors, sessions, content, and platform activity.",
          icon: TrendingUp,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support your learning platform with troubleshooting, optimization, integrations, new features, and educational workflow improvements.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "Tutor App Solutions for Different Learning Models",
      description:
        "Custom tutoring platforms tailored to different education models, student audiences, teaching formats, subjects, and learning workflows.",
      items: [
        {
          title: "Academic Tutoring",
          description:
            "Connect students with subject tutors through discovery, booking, live classes, learning resources, and progress tracking.",
          icon: GraduationCap,
        },
        {
          title: "Language Learning",
          description:
            "Build platforms for language tutor discovery, scheduled lessons, live communication, learning resources, and student progress.",
          icon: Languages,
        },
        {
          title: "Professional Skill Training",
          description:
            "Connect learners with instructors for suitable professional, technical, creative, and career-focused learning sessions.",
          icon: BriefcaseBusiness,
        },
        {
          title: "Test Preparation",
          description:
            "Support tutor-led preparation programs with bookings, live sessions, study materials, learning plans, and progress workflows.",
          icon: ClipboardCheck,
        },
        {
          title: "Music & Creative Learning",
          description:
            "Create platforms connecting learners with suitable music, art, design, and other creative educators.",
          icon: Palette,
        },
        {
          title: "Multi-Tutor Marketplaces",
          description:
            "Operate learning marketplaces with multiple educators, subjects, pricing models, bookings, payments, and centralized administration.",
          icon: Users,
        },
      ],
    },

    portfolio: {
      title: "Tutor App Development Success Stories",
      description:
        "Learning platforms focused on tutor discovery, booking, live sessions, educational content, payments, progress tracking, and administration.",
      items: [
        {
          title: "Online Tutor Marketplace",
          description:
            "Developed student and tutor experiences with educator discovery, profiles, availability, booking, live classes, payments, ratings, and centralized administration.",
          image: "/project-image.webp",
        },
        {
          title: "Live Learning Platform",
          description:
            "Built a digital education platform connecting scheduled sessions, real-time video learning, educational content, student progress, tutor management, and reporting.",
          image: "/project-image.webp",
        },
        {
          title: "Professional Training Marketplace",
          description:
            "Created a multi-instructor learning platform with discovery, booking, session management, payments, learning resources, dashboards, and administrative controls.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Tutor App Developers",
      description:
        "Partner with our developers to design, build, integrate, optimize, and support tutor platforms connecting students, educators, live learning, scheduling, payments, and progress management.",
      cta: "Let's Build Your Tutor App",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Education businesses trust Skand Tech Hub to turn tutoring and learning workflows into connected digital platforms.",
      items: [
        {
          name: "Emma Richardson",
          company: "LearnBridge",
          location: "United Kingdom",
          review:
            "The platform made it much easier for students to discover tutors, check availability, and book sessions. Our educators also have a much clearer way to manage classes and schedules.",
        },
        {
          name: "Matthew Collins",
          company: "SkillPath Learning",
          location: "United States",
          review:
            "Skand Tech Hub connected our booking, live sessions, payments, and student progress workflows into one product. Managing both instructors and learners is now far more structured.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description: "Common questions about our Tutor App Development services.",
      items: [
        {
          question: "What applications can be included in a tutor platform?",
          answer:
            "Depending on your learning model, the platform can include student applications, tutor applications or portals, administrative dashboards, live classroom functionality, booking systems, payments, content management, and backend services.",
        },
        {
          question: "Can students search and book tutors?",
          answer:
            "Yes. Students can discover suitable tutors using subjects, profiles, expertise, availability, and other relevant criteria before selecting available sessions and completing booking workflows.",
        },
        {
          question: "Can you integrate live video classes?",
          answer:
            "Yes. Suitable real-time communication technologies such as WebRTC can be used to support video, audio, and interactive live learning experiences.",
        },
        {
          question: "Can tutors manage their own availability?",
          answer:
            "Yes. Tutor functionality can include suitable availability schedules, booking management, upcoming sessions, class information, learning resources, and earnings visibility.",
        },
        {
          question: "Can payments be integrated into the tutor app?",
          answer:
            "Yes. Suitable payment providers can be integrated for session payments, platform charges, transaction records, tutor earnings, and related payment workflows.",
        },
        {
          question: "Do you provide ongoing tutor app support?",
          answer:
            "Yes. We can provide troubleshooting, optimization, integrations, new learning functionality, workflow improvements, and ongoing tutor platform development.",
        },
      ],
    },
  },
  // Astrology App Development
  {
    title: "Astrology App Development",
    slug: "astrology-app-development",
    group: "On Demand Mobile App",
    icon: Compass,

    seo: {
      title: "Astrology App Development Services",
      description:
        "Build custom astrology apps with astrologer discovery, chat and call consultations, wallet billing, horoscope content, availability management, and real-time engagement.",
      keywords: [
        "Astrology App Development",
        "Astrology App Development Services",
        "Astrologer Consultation App",
        "Online Astrology App Development",
        "Astrology App Developers",
        "Astrology App Development Company",
      ],
    },

    hero: {
      title: "Astrology App Development for Real-Time Consultations",
      description:
        "Create a trusted consultation platform with real-time sessions, wallet flows, and content engagement.",
      highlights: [
        "Expert Availability Systems",
        "Wallet & Consultation Billing",
        "Chat & Call Consultations",
        "Content & Notification Engine",
      ],
    },

    whyChoose: {
      title: "Why Invest in a Custom Astrology App?",
      description:
        "A custom astrology platform connects users with astrologers through real-time consultations while bringing availability, wallets, billing, content, notifications, and platform operations into one digital ecosystem.",
      items: [
        {
          title: "Real-Time Consultations",
          description:
            "Connect users and astrologers through suitable chat, audio, and video consultation experiences.",
          icon: MessageCircle,
        },
        {
          title: "Expert Discovery & Availability",
          description:
            "Help users discover astrologers based on relevant expertise, profiles, languages, availability, ratings, and consultation options.",
          icon: UserRoundSearch,
        },
        {
          title: "Wallet-Based Billing",
          description:
            "Manage suitable wallet balances, consultation charges, transaction records, recharges, and expert earnings.",
          icon: Wallet,
        },
        {
          title: "Continuous User Engagement",
          description:
            "Keep users engaged through suitable horoscope content, notifications, personalized experiences, and consultation activity.",
          icon: Bell,
        },
      ],
    },

    services: {
      title: "Our Astrology App Development Services",
      description:
        "End-to-end astrology app development covering user applications, astrologer portals, real-time consultations, wallets, horoscope content, notifications, and administrative operations.",
      items: [
        {
          title: "Astrology User App Development",
          description:
            "Create user experiences for astrologer discovery, consultations, wallet management, horoscope content, profiles, and consultation history.",
          icon: Smartphone,
          features: [
            "Astrologer Discovery",
            "Chat & Call Consultation",
            "Wallet Management",
            "Horoscope Content",
            "Consultation History",
          ],
        },
        {
          title: "Astrologer App & Portal Development",
          description:
            "Provide astrologers with tools to manage profiles, availability, consultation requests, sessions, earnings, and account activity.",
          icon: UserRoundSearch,
          features: [
            "Astrologer Profiles",
            "Availability Controls",
            "Consultation Requests",
            "Session Management",
            "Earnings Overview",
          ],
        },
        {
          title: "Chat & Call Consultation Development",
          description:
            "Build suitable real-time consultation workflows with messaging, audio, video, session timing, and billing integration.",
          icon: Video,
          features: [
            "Real-Time Chat",
            "Audio Calls",
            "Video Calls",
            "Session Timing",
            "Consultation Billing",
          ],
        },
        {
          title: "Astrology Admin Panel",
          description:
            "Centralize platform operations with tools for managing users, astrologers, consultations, wallets, transactions, content, and reporting.",
          icon: LayoutDashboard,
          features: [
            "User Management",
            "Astrologer Management",
            "Consultation Monitoring",
            "Wallet & Transaction Management",
            "Content Management",
          ],
        },
      ],
    },

    features: {
      title: "Key Astrology App Features",
      description:
        "Core functionality for connecting users and astrologers through discovery, real-time consultations, wallets, horoscope content, and engagement workflows.",
      items: [
        {
          title: "Astrologer Discovery",
          description:
            "Allow users to discover suitable astrologers using expertise, profiles, languages, ratings, availability, and consultation options.",
          icon: Search,
        },
        {
          title: "Chat Consultations",
          description:
            "Enable suitable real-time messaging sessions between users and available astrologers with structured consultation workflows.",
          icon: MessageCircle,
        },
        {
          title: "Audio & Video Calls",
          description:
            "Support suitable real-time voice and video consultations with session status and duration management.",
          icon: Video,
        },
        {
          title: "Wallet & Billing",
          description:
            "Manage wallet recharges, balances, consultation charges, transactions, deductions, and relevant payment workflows.",
          icon: Wallet,
        },
        {
          title: "Horoscope & Astrology Content",
          description:
            "Publish suitable horoscope, zodiac, astrology, educational, and engagement content through structured content workflows.",
          icon: BookOpen,
        },
        {
          title: "Push Notifications",
          description:
            "Send relevant consultation updates, availability alerts, content notifications, wallet information, and platform communications.",
          icon: Bell,
        },
      ],
    },

    process: {
      title: "Our Astrology App Development Process",
      description:
        "A structured platform development process focused on user and astrologer journeys, consultation workflows, wallet billing, real-time communication, content, and administration.",
      items: [
        {
          title: "Astrology Platform Discovery",
          description:
            "Understand your consultation model, target users, astrologer categories, pricing, availability, wallet rules, content, payments, and operational requirements.",
        },
        {
          title: "Consultation & System Architecture",
          description:
            "Define user roles, astrologer discovery, availability, chat and call workflows, wallet billing, payments, content, notifications, APIs, and platform architecture.",
        },
        {
          title: "User & Astrologer UI/UX Design",
          description:
            "Design discovery, profiles, consultation screens, wallets, content, astrologer dashboards, and administrative experiences.",
        },
        {
          title: "Astrology Platform Development",
          description:
            "Develop user and astrologer applications, backend services, real-time consultations, wallets, payments, content, dashboards, and required integrations.",
        },
        {
          title: "Consultation & Billing Testing",
          description:
            "Test availability, chat, calls, session timing, wallet deductions, payments, notifications, permissions, content, and consultation workflows.",
        },
        {
          title: "Deployment & Support",
          description:
            "Launch the astrology platform and provide ongoing technical support, optimization, integrations, engagement features, and platform improvements.",
        },
      ],
    },

    benefits: {
      title: "Benefits of Custom Astrology App Development",
      description:
        "Create a connected consultation platform that simplifies access to astrologers while providing structured billing, engagement, content, and operational management.",
      items: [
        {
          title: "On-Demand Expert Access",
          description:
            "Allow users to discover available astrologers and connect through suitable chat, audio, or video consultation options.",
          icon: UserRoundSearch,
        },
        {
          title: "Flexible Consultation Experiences",
          description:
            "Support multiple suitable communication formats while managing consultation requests, sessions, timing, and statuses.",
          icon: MessageCircle,
        },
        {
          title: "Structured Wallet Billing",
          description:
            "Centralize suitable recharges, balances, consultation deductions, transactions, and expert earning records.",
          icon: Wallet,
        },
        {
          title: "Improved User Engagement",
          description:
            "Keep users connected with horoscope content, notifications, expert availability, consultation history, and relevant platform activity.",
          icon: Bell,
        },
        {
          title: "Centralized Platform Management",
          description:
            "Manage users, astrologers, consultations, wallets, transactions, content, and operational activity from one platform.",
          icon: LayoutDashboard,
        },
        {
          title: "Scalable Consultation Operations",
          description:
            "Expand users, astrologers, consultation types, content, functionality, and platform capacity as the business grows.",
          icon: TrendingUp,
        },
      ],
    },

    techStack: {
      title: "Astrology App Development Technologies",
      description:
        "Technologies used to build cross-platform consultation experiences, real-time communication, notifications, and wallet payment workflows.",
      technologies: ["React Native", "Firebase", "WebRTC", "Razorpay"],
    },

    whyUs: {
      title: "Why Choose Skand Tech Hub?",
      description:
        "We approach astrology app development as a complete consultation ecosystem, connecting user experiences with astrologer operations, real-time communication, wallet billing, content, and platform administration.",
      items: [
        {
          title: "Multi-Role Platform Development",
          description:
            "Build coordinated experiences for users, astrologers, administrators, content teams, and other required platform roles.",
          icon: Users,
        },
        {
          title: "Real-Time Consultation Engineering",
          description:
            "Implement suitable chat, audio, video, session timing, availability, and real-time communication workflows.",
          icon: Video,
        },
        {
          title: "Wallet & Billing Architecture",
          description:
            "Design structured workflows around recharges, balances, consultation pricing, deductions, transactions, and expert earnings.",
          icon: Wallet,
        },
        {
          title: "Content & Engagement Systems",
          description:
            "Build suitable horoscope publishing, notifications, consultation reminders, and ongoing user engagement workflows.",
          icon: Bell,
        },
        {
          title: "Scalable Platform Engineering",
          description:
            "Build technical foundations capable of supporting increasing users, astrologers, consultations, transactions, and content activity.",
          icon: TrendingUp,
        },
        {
          title: "Ongoing Technical Support",
          description:
            "Support your astrology platform with troubleshooting, optimization, integrations, new functionality, and consultation workflow improvements.",
          icon: MessageCircle,
        },
      ],
    },

    industrySolutions: {
      title: "Astrology App Solutions for Different Business Models",
      description:
        "Custom astrology platforms tailored to different consultation models, expert networks, content strategies, and digital astrology services.",
      items: [
        {
          title: "Astrologer Marketplaces",
          description:
            "Connect users with multiple astrologers through profiles, availability, consultations, wallet billing, ratings, and centralized administration.",
          icon: Users,
        },
        {
          title: "Personal Astrology Brands",
          description:
            "Build branded consultation platforms for individual astrologers or astrology businesses offering digital services and content.",
          icon: Sparkles,
        },
        {
          title: "Horoscope Platforms",
          description:
            "Create content-driven applications around suitable daily, weekly, monthly, zodiac, and astrology-related content experiences.",
          icon: BookOpen,
        },
        {
          title: "Live Consultation Platforms",
          description:
            "Focus on real-time astrologer availability, chat, audio, video, wallet billing, session management, and consultation history.",
          icon: Video,
        },
        {
          title: "Spiritual Consultation Platforms",
          description:
            "Create suitable expert consultation marketplaces with discovery, booking, communication, payments, content, and engagement workflows.",
          icon: HeartHandshake,
        },
        {
          title: "Multi-Language Astrology Platforms",
          description:
            "Support suitable astrologer profiles, content, consultation experiences, and user interfaces across multiple languages and audiences.",
          icon: Languages,
        },
      ],
    },

    portfolio: {
      title: "Astrology App Development Success Stories",
      description:
        "Consultation platforms focused on astrologer discovery, real-time sessions, wallet billing, horoscope content, notifications, and centralized administration.",
      items: [
        {
          title: "Astrologer Consultation Marketplace",
          description:
            "Developed user and astrologer experiences with expert discovery, availability, chat and call consultations, wallet billing, ratings, notifications, and centralized administration.",
          image: "/project-image.webp",
        },
        {
          title: "Live Astrology Consultation Platform",
          description:
            "Built a real-time consultation system connecting astrologer availability, session requests, chat, audio calls, wallet deductions, transaction records, and expert earnings.",
          image: "/project-image.webp",
        },
        {
          title: "Astrology Content & Consultation App",
          description:
            "Created a platform combining horoscope content, astrologer profiles, consultation workflows, wallets, notifications, user engagement, and administrative controls.",
          image: "/project-image.webp",
        },
      ],
    },

    hire: {
      title: "Hire Astrology App Developers",
      description:
        "Partner with our developers to design, build, integrate, optimize, and support astrology platforms with expert discovery, consultations, wallets, content, and real-time communication.",
      cta: "Let's Build Your Astrology App",
    },

    testimonials: {
      title: "What Our Clients Say",
      description:
        "Astrology businesses trust Skand Tech Hub to turn consultation, billing, expert management, and engagement workflows into connected digital platforms.",
      items: [
        {
          name: "Arjun Mehta",
          company: "CosmicPath",
          location: "India",
          review:
            "The platform brought our astrologer availability, consultations, wallet system, and user activity into one place. Managing both experts and customers has become much more structured.",
        },
        {
          name: "Rahul Verma",
          company: "AstroConnect",
          location: "India",
          review:
            "Skand Tech Hub understood that consultation timing and wallet billing had to work together reliably. The chat, call, notification, and transaction workflows now feel like one connected system.",
        },
      ],
    },

    faq: {
      title: "Frequently Asked Questions",
      description:
        "Common questions about our Astrology App Development services.",
      items: [
        {
          question:
            "What applications can be included in an astrology platform?",
          answer:
            "Depending on your business model, the platform can include user applications, astrologer applications or portals, administrative dashboards, chat and call consultation systems, wallets, payments, horoscope content, notifications, and backend services.",
        },
        {
          question: "Can users chat or call astrologers in real time?",
          answer:
            "Yes. Suitable real-time technologies can support chat, audio, and video consultations with availability, session status, timing, and billing workflows.",
        },
        {
          question: "Can the app include a wallet system?",
          answer:
            "Yes. The platform can include suitable wallet recharge, balance, consultation deduction, transaction history, refund, and expert earning workflows.",
        },
        {
          question: "Can astrologers manage their availability?",
          answer:
            "Yes. Astrologers can have suitable controls for online status, availability, consultation requests, sessions, profiles, earnings, and other required activities.",
        },
        {
          question: "Can we publish horoscope content in the app?",
          answer:
            "Yes. The platform can include suitable content management workflows for horoscope, zodiac, astrology, educational, promotional, and engagement content.",
        },
        {
          question: "Do you provide ongoing astrology app support?",
          answer:
            "Yes. We can provide troubleshooting, optimization, integrations, consultation improvements, engagement features, and ongoing astrology platform development.",
        },
      ],
    },
  },
];
